import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useParams,
} from "react-router";

import { RequireAuth } from "@/auth/RequireAuth";
import { RealtimeProvider } from "@/realtime/RealtimeProvider";
import { DetalleEmpresa } from "@/routes/DetalleEmpresa";
import { ContactDetail } from "@/routes/ContactDetail";
import { Actividades } from "@/routes/Actividades";
import { Agenda } from "@/routes/Agenda";
import { Correo } from "@/routes/Correo";
import { Inicio } from "@/routes/Inicio";
import { Inbox } from "@/routes/Inbox";
import { Llamar } from "@/routes/Llamar";
import { Login } from "@/routes/Login";
import { NotFound } from "@/routes/NotFound";
import { NuevaLlamada } from "@/routes/NuevaLlamada";
import { PasswordRecovery } from "@/routes/PasswordRecovery";
import { PasswordReset } from "@/routes/PasswordReset";
import { Plantillas } from "@/routes/Plantillas";
import { Prospectos } from "@/routes/Prospectos";
import { Reception } from "@/routes/Reception";
import { Settings } from "@/routes/Settings";
import { Shell } from "@/routes/Shell";
import { CapturaTarjetas } from "@/routes/CapturaTarjetas";
import { ImportacionTarjetas } from "@/routes/ImportacionTarjetas";
import { ImportarTarjetas } from "@/routes/ImportarTarjetas";
import { Tarjetas } from "@/routes/Tarjetas";
import { SoftphoneProvider } from "@/telephony/SoftphoneProvider";

/** `/empresas/482` → `/prospectos/482`, keeping the id (D23). */
function RedirectToProspecto() {
  const { id } = useParams();
  return <Navigate to={`/prospectos/${id}`} replace />;
}

/**
 * Everything behind the session, as a layout route.
 *
 * It stays mounted for as long as the browser is on any screen under it, which
 * is what lets a call survive navigation: selling means clicking around the
 * CRM while talking (.docs/04 §6).
 */
function SignedIn() {
  return (
    <RequireAuth>
      {/* Inside RequireAuth: the socket authenticates with the session
          cookie, so opening one before we know there is a session just
          earns a 4401 and a redirect we were about to do anyway. */}
      <RealtimeProvider>
        <SoftphoneProvider>
          <Outlet />
        </SoftphoneProvider>
      </RealtimeProvider>
    </RequireAuth>
  );
}

/**
 * Created once, outside the component, or every render throws away the cache.
 *
 * `retry: false` is the deliberate choice for a session-cookie SPA: the failure
 * that matters is 401, `client` has already begun navigating to login, and
 * retrying only means three more requests behind a redirect. Individual queries
 * that genuinely benefit from a retry can opt in.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* The three screens there is no session for yet. Outside `SignedIn`
              on purpose: under it, `me` would answer 401 and the 401 handler
              would send the browser to `/entrar` — from `/entrar`. */}
          <Route path="entrar" element={<Login />} />
          <Route path="recuperar" element={<PasswordRecovery />} />
          {/* Where the link in a reset email points (`HEADLESS_FRONTEND_URLS`
              in the Django settings). The two have to agree. */}
          <Route path="restablecer/:key" element={<PasswordReset />} />
          <Route element={<SignedIn />}>
            <Route element={<Shell />}>
              <Route index element={<Inicio />} />
              {/* Spanish paths: these are URLs sellers read, type and share.
                  D18 governs identifiers in code, not what a user sees. */}
              <Route path="prospectos" element={<Prospectos />} />
              <Route path="agenda" element={<Agenda />} />
              <Route path="actividades" element={<Actividades />} />
              <Route path="llamar" element={<Llamar />} />
              {/* D23. `/empresas` was the P5 name and is in bookmarks and
                  in chat history. nginx serves `/index.html` for any path,
                  so these never 404 at the HTTP layer — what breaks is
                  React Router falling through to NotFound. `replace` so
                  Back does not bounce between the two names. */}
              <Route path="empresas" element={<Navigate to="/prospectos" replace />} />
              <Route path="empresas/:id" element={<RedirectToProspecto />} />
              <Route path="prospectos/:id" element={<DetalleEmpresa />} />
              <Route path="contactos/:id" element={<ContactDetail />} />
              <Route path="correo" element={<Correo />} />
              <Route path="conversaciones" element={<Inbox />} />
              <Route path="recepcion" element={<Reception />} />
              <Route path="recepcion/nueva" element={<NuevaLlamada />} />
              <Route path="plantillas" element={<Plantillas />} />
              <Route path="tarjetas" element={<Tarjetas />} />
              <Route path="tarjetas/captura" element={<CapturaTarjetas />} />
              <Route path="tarjetas/importar" element={<ImportarTarjetas />} />
              <Route
                path="tarjetas/importaciones/:id"
                element={<ImportacionTarjetas />}
              />
              <Route path="ajustes" element={<Settings />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
